import { Device } from "@/types/device";
import { NormalizedRelease } from "@/types/sofa";

const safeLower = (v: unknown): string => (typeof v === "string" ? v.toLowerCase() : "");
const safeNum = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const safeArr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/**
 * Compare two version strings (e.g. "26.3" vs "26.4.1").
 * Returns -1 if a < b, 0 if equal, 1 if a > b.
 * Tolerates non-string inputs by coercing to "".
 */
export function compareVersions(a: unknown, b: unknown): number {
  const sa = typeof a === "string" ? a : "";
  const sb = typeof b === "string" ? b : "";
  const partsA = sa.split(".").map(Number);
  const partsB = sb.split(".").map(Number);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const numA = partsA[i] || 0;
    const numB = partsB[i] || 0;
    if (numA < numB) return -1;
    if (numA > numB) return 1;
  }
  return 0;
}

/**
 * Determine which devices are vulnerable to a given release.
 */
export function getImpactedDevices(release: NormalizedRelease, devices: Device[]): Device[] {
  const releasePlatform = safeLower(release?.productName);
  if (!releasePlatform || !release?.productVersion) return [];

  return safeArr<Device>(devices).filter(device => {
    const deviceOS = safeLower(device?.os_type);

    const platformMatch =
      (releasePlatform === "macos" && deviceOS === "macos") ||
      (releasePlatform === "ios" && (deviceOS === "ios" || deviceOS === "ipados")) ||
      (releasePlatform === "ipados" && deviceOS === "ipados");

    if (!platformMatch) return false;

    const deviceVersion = typeof device?.os_version === "string" ? device.os_version : "";
    if (!deviceVersion) return false;

    if (compareVersions(deviceVersion, release.productVersion) >= 0) return false;

    const supported = safeArr<string>(release.supportedDevices);
    if (release.deviceScope !== "universal" && supported.length > 0 && device.product) {
      return supported.includes(device.product);
    }

    return true;
  });
}

/**
 * For a list of releases, compute impacted device counts.
 */
export function enrichReleasesWithFleetData(
  releases: NormalizedRelease[],
  devices: Device[]
): NormalizedRelease[] {
  return safeArr<NormalizedRelease>(releases).map(release => ({
    ...release,
    impactedDeviceCount: getImpactedDevices(release, devices).length,
  }));
}

/**
 * Calculate fleet exposure summary.
 */
export function getFleetExposure(releases: NormalizedRelease[], devices: Device[]) {
  const safeReleases = safeArr<NormalizedRelease>(releases);
  const safeDevices = safeArr<Device>(devices);

  const latestVersions: Record<string, string> = {};
  for (const release of safeReleases) {
    const platform = safeLower(release?.productName);
    const version = typeof release?.productVersion === "string" ? release.productVersion : "";
    if (!platform || !version) continue;
    if (!latestVersions[platform] || compareVersions(version, latestVersions[platform]) > 0) {
      latestVersions[platform] = version;
    }
  }

  let upToDate = 0;
  let outdated = 0;
  let unknown = 0;

  for (const device of safeDevices) {
    const deviceOS = safeLower(device?.os_type);
    const latestVersion = latestVersions[deviceOS];
    const dv = typeof device?.os_version === "string" ? device.os_version : "";
    if (!latestVersion || !dv) {
      unknown++;
      continue;
    }
    if (compareVersions(dv, latestVersion) >= 0) {
      upToDate++;
    } else {
      outdated++;
    }
  }

  return { upToDate, outdated, unknown, total: safeDevices.length };
}

/** A recommended action item for the fleet */
export interface RecommendedAction {
  id: string;
  urgency: "critical" | "warning" | "ok";
  platform: string;
  title: string;
  description: string;
  affectedDevices: Device[];
  targetVersion: string;
  activelyExploitedCount: number;
}

/**
 * Generate prioritized recommended actions by cross-referencing releases with devices.
 * Groups by platform and returns actionable items sorted by urgency.
 */
export function generateRecommendedActions(
  releases: NormalizedRelease[],
  devices: Device[]
): RecommendedAction[] {
  const safeReleases = safeArr<NormalizedRelease>(releases);
  const safeDevices = safeArr<Device>(devices);

  // Find the latest release per platform
  const latestByPlatform: Record<string, NormalizedRelease> = {};
  for (const release of safeReleases) {
    const platform = safeLower(release?.productName);
    const version = typeof release?.productVersion === "string" ? release.productVersion : "";
    if (!platform || !version) continue;
    const current = latestByPlatform[platform];
    if (!current || compareVersions(version, current.productVersion) > 0) {
      latestByPlatform[platform] = release;
    }
  }

  const actions: RecommendedAction[] = [];

  for (const [platform, latestRelease] of Object.entries(latestByPlatform)) {
    // Find all devices on this platform that are outdated
    const platformDevices = safeDevices.filter(d => {
      const os = safeLower(d?.os_type);
      return (
        (platform === "macos" && os === "macos") ||
        (platform === "ios" && (os === "ios" || os === "ipados")) ||
        (platform === "ipados" && os === "ipados")
      );
    });

    const outdatedDevices = platformDevices.filter(
      d => typeof d?.os_version === "string" && d.os_version && compareVersions(d.os_version, latestRelease.productVersion) < 0
    );

    // Check if any actively exploited CVEs affect these outdated devices
    const exploitedReleases = safeReleases.filter(
      r => safeLower(r?.productName) === platform && safeNum(r?.activelyExploitedCount) > 0
    );
    const totalExploited = exploitedReleases.reduce((sum, r) => sum + safeNum(r?.activelyExploitedCount), 0);

    // Devices exposed to actively exploited CVEs (running version older than a release with exploited CVEs)
    const exploitedDevices = outdatedDevices.filter(d => {
      return exploitedReleases.some(
        r => typeof d?.os_version === "string" && d.os_version && compareVersions(d.os_version, r.productVersion) < 0
      );
    });

    const displayPlatform =
      platform === "macos" ? "macOS" :
      platform === "ios" ? "iOS" :
      platform === "ipados" ? "iPadOS" :
      platform.charAt(0).toUpperCase() + platform.slice(1);

    if (exploitedDevices.length > 0) {
      actions.push({
        id: `${platform}-exploited`,
        urgency: "critical",
        platform: displayPlatform,
        title: `${exploitedDevices.length} device${exploitedDevices.length !== 1 ? "s" : ""} exposed to actively exploited vulnerabilities`,
        description: `Update to ${displayPlatform} ${latestRelease.productVersion} immediately. ${totalExploited} actively exploited CVE${totalExploited !== 1 ? "s" : ""} affect${totalExploited === 1 ? "s" : ""} these devices.`,
        affectedDevices: exploitedDevices,
        targetVersion: latestRelease.productVersion,
        activelyExploitedCount: totalExploited,
      });
    }

    // Remaining outdated devices (not exploited but behind)
    const nonExploitedOutdated = outdatedDevices.filter(
      d => !exploitedDevices.includes(d)
    );

    if (nonExploitedOutdated.length > 0) {
      actions.push({
        id: `${platform}-outdated`,
        urgency: "warning",
        platform: displayPlatform,
        title: `${nonExploitedOutdated.length} device${nonExploitedOutdated.length !== 1 ? "s" : ""} behind on ${displayPlatform} updates`,
        description: `Schedule updates to ${displayPlatform} ${latestRelease.productVersion} during your next maintenance window.`,
        affectedDevices: nonExploitedOutdated,
        targetVersion: latestRelease.productVersion,
        activelyExploitedCount: 0,
      });
    }

    if (outdatedDevices.length === 0 && platformDevices.length > 0) {
      actions.push({
        id: `${platform}-ok`,
        urgency: "ok",
        platform: displayPlatform,
        title: `${displayPlatform} fleet is fully up to date`,
        description: `All ${platformDevices.length} device${platformDevices.length !== 1 ? "s" : ""} are running ${displayPlatform} ${latestRelease.productVersion} or later.`,
        affectedDevices: [],
        targetVersion: latestRelease.productVersion,
        activelyExploitedCount: 0,
      });
    }
  }

  // Sort: critical first, then warning, then ok
  const urgencyOrder = { critical: 0, warning: 1, ok: 2 };
  actions.sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]);

  return actions;
}
